import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useDropzone } from "react-dropzone";
import { AppLayout } from "@/components/AppLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Building2, 
  Upload, 
  Save, 
  Loader2,
  Image as ImageIcon,
  X
} from "lucide-react";
import { toast } from "sonner";
import { EmailSettingsCard } from "@/components/EmailSettingsCard";
import { AISettingsCard } from "@/components/AISettingsCard";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function OrganizationSettings({ user, setUser }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [organization, setOrganization] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    address: "",
    phone: "",
    email: ""
  });
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);

  useEffect(() => {
    fetchOrganization();
  }, []);

  const fetchOrganization = async () => {
    try {
      const response = await fetch(`${API_URL}/api/organizations/me`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setOrganization(data);
        setFormData({
          name: data.name || "",
          address: data.address || "",
          phone: data.phone || "",
          email: data.email || ""
        });
        if (data.logo_url) {
          setLogoPreview(`${API_URL}${data.logo_url}`);
        }
      }
    } catch (error) {
      console.error('Error fetching organization:', error);
    } finally {
      setLoading(false);
    }
  };

  const onDrop = useCallback((acceptedFiles) => {
    const file = acceptedFiles[0];
    if (file) {
      setLogoFile(file);
      setLogoPreview(URL.createObjectURL(file));
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.png', '.jpg', '.jpeg', '.svg']
    },
    maxFiles: 1
  });

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error("Le nom de l'organisation est requis");
      return;
    }

    setSaving(true);

    try {
      let response;
      
      if (organization) {
        // Update existing
        response = await fetch(`${API_URL}/api/organizations/${organization.organization_id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(formData)
        });
      } else {
        // Create new
        response = await fetch(`${API_URL}/api/organizations`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(formData)
        });
      }

      if (response.ok) {
        const data = await response.json();
        setOrganization(data);
        
        // Upload logo if there's a new one
        if (logoFile) {
          await uploadLogo(data.organization_id);
        }
        
        toast.success(organization ? "Organisation mise à jour" : "Organisation créée");
        
        // Refresh user data
        const userResponse = await fetch(`${API_URL}/api/auth/me`, {
          credentials: 'include'
        });
        if (userResponse.ok) {
          const userData = await userResponse.json();
          setUser(userData);
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || "Erreur lors de la sauvegarde");
      }
    } catch (error) {
      toast.error("Erreur lors de la sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  const uploadLogo = async (orgId) => {
    if (!logoFile) return;

    setUploadingLogo(true);
    
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', logoFile);

      const response = await fetch(`${API_URL}/api/organizations/${orgId}/logo`, {
        method: 'POST',
        credentials: 'include',
        body: formDataUpload
      });

      if (response.ok) {
        const data = await response.json();
        setLogoPreview(`${API_URL}${data.logo_url}`);
        setLogoFile(null);
        toast.success("Logo mis à jour");
      } else {
        toast.error("Erreur lors de l'upload du logo");
      }
    } catch (error) {
      toast.error("Erreur lors de l'upload du logo");
    } finally {
      setUploadingLogo(false);
    }
  };

  const removeLogo = () => {
    setLogoFile(null);
    setLogoPreview(organization?.logo_url ? `${API_URL}${organization.logo_url}` : null);
  };

  if (loading) {
    return (
      <AppLayout user={user} setUser={setUser}>
        <div className="flex items-center justify-center h-screen">
          <div className="spinner w-8 h-8"></div>
        </div>
      </AppLayout>
    );
  }

  // Organization settings page is restricted to administrators
  if (user?.role !== "admin") {
    return (
      <AppLayout user={user} setUser={setUser}>
        <div className="p-8 max-w-3xl mx-auto" data-testid="org-access-denied">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Building2 className="w-5 h-5" />
                Accès réservé
              </CardTitle>
              <CardDescription>
                Seuls les administrateurs peuvent accéder aux paramètres de l'organisation.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button onClick={() => navigate("/dashboard")} data-testid="back-to-dashboard-btn">
                Retour au tableau de bord
              </Button>
            </CardContent>
          </Card>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout user={user} setUser={setUser}>
      <div className="p-8 max-w-3xl mx-auto" data-testid="organization-settings-page">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-heading text-3xl font-semibold tracking-tight mb-2">
            {organization ? "Paramètres de l'organisation" : "Créer votre organisation"}
          </h1>
          <p className="text-muted-foreground">
            {organization 
              ? "Gérez les informations de votre organisation" 
              : "Configurez votre organisation pour commencer à gérer vos contrats"}
          </p>
        </div>

        <div className="space-y-6">
          {/* Organization Info */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Building2 className="w-5 h-5" />
                Informations générales
              </CardTitle>
              <CardDescription>
                Ces informations peuvent être utilisées dans vos contrats
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Nom de l'organisation *</Label>
                <Input
                  id="name"
                  placeholder="Ex: Cabinet Dupont & Associés"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  data-testid="org-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="contact@organisation.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  data-testid="org-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Téléphone</Label>
                <Input
                  id="phone"
                  placeholder="+33 1 23 45 67 89"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  data-testid="org-phone-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="address">Adresse</Label>
                <Input
                  id="address"
                  placeholder="123 Rue de la Paix, 75001 Paris"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  data-testid="org-address-input"
                />
              </div>
            </CardContent>
          </Card>

          {/* Logo Upload */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <ImageIcon className="w-5 h-5" />
                Logo de l'organisation
              </CardTitle>
              <CardDescription>
                Ce logo sera affiché sur la première page de vos contrats
              </CardDescription>
            </CardHeader>
            <CardContent>
              {logoPreview ? (
                <div className="space-y-4">
                  <div className="relative inline-block">
                    <div className="p-4 border border-border rounded-lg bg-white">
                      <img 
                        src={logoPreview} 
                        alt="Logo preview" 
                        className="max-h-32 object-contain"
                        data-testid="logo-preview"
                      />
                    </div>
                    <Button
                      variant="destructive"
                      size="icon"
                      className="absolute -top-2 -right-2 h-6 w-6"
                      onClick={removeLogo}
                      data-testid="remove-logo-btn"
                    >
                      <X className="w-3 h-3" />
                    </Button>
                  </div>
                  {logoFile && (
                    <p className="text-sm text-muted-foreground">
                      Nouveau fichier: {logoFile.name}
                    </p>
                  )}
                  <div
                    {...getRootProps()}
                    className="upload-area cursor-pointer"
                    data-testid="logo-dropzone"
                  >
                    <input {...getInputProps()} />
                    <Upload className="w-8 h-8 text-muted-foreground mb-2" />
                    <p className="text-sm text-muted-foreground">
                      Remplacer le logo
                    </p>
                  </div>
                </div>
              ) : (
                <div
                  {...getRootProps()}
                  className={`upload-area ${isDragActive ? 'drag-active' : ''}`}
                  data-testid="logo-dropzone"
                >
                  <input {...getInputProps()} />
                  <Upload className="w-12 h-12 text-muted-foreground mb-4" />
                  <p className="text-sm font-medium mb-1">
                    {isDragActive ? "Déposez l'image ici" : "Glissez-déposez votre logo"}
                  </p>
                  <p className="text-xs text-muted-foreground mb-4">
                    PNG, JPG ou SVG (max 2MB)
                  </p>
                  <Button type="button" variant="outline" size="sm">
                    Parcourir
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Email Settings (admin only) */}
          {organization && user?.role === "admin" && <EmailSettingsCard />}

          {/* AI Keys Settings (admin only) */}
          {organization && user?.role === "admin" && <AISettingsCard />}

          {/* Actions */}
          <div className="flex justify-end">
            <Button 
              onClick={handleSave} 
              disabled={saving || uploadingLogo}
              data-testid="save-org-btn"
            >
              {(saving || uploadingLogo) ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              {organization ? "Mettre à jour" : "Créer l'organisation"}
            </Button>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
